/* Refresh display-only cart sections after a back/forward restoration or
   opening the bag after an app/another tab changes the server cart.
   Never changes cart contents or emits product/cart analytics. */
(() => {
  'use strict';
  let pending = null;

  const cancelPending = () => {
    pending?.abort();
    pending = null;
  };

  // Do not let a slower restore response overwrite a newer shopper action.
  document.addEventListener('submit', (event) => {
    if (event.target.matches?.('form[action*="/cart"]')) cancelPending();
  }, true);
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('cart-items, cart-drawer-items')) cancelPending();
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('cart-remove-button, button[name="add"], .cm-add-form button[type="submit"], .cm-upgrade-form button[type="submit"]')) {
      cancelPending();
      return;
    }
    if (event.target.closest?.('#cart-icon-bubble')) return refreshCart();
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.code === 'Space' && event.target.closest?.('#cart-icon-bubble')) return refreshCart();
  }, true);
  window.addEventListener('pagehide', cancelPending);

  window.addEventListener('pageshow', (event) => {
    // WebKit can restore HTTP-cached history documents without a bfcache hit.
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    return refreshCart();
  });

  async function refreshCart() {
    if (pending) return;
    const drawer = document.querySelector('cart-drawer');
    const content = drawer?.querySelector('#CartDrawer');
    const bubble = document.getElementById('cart-icon-bubble');
    if (!drawer || !content || !bubble) return;

    const before = content.innerHTML;
    const request = new AbortController();
    pending = request;
    const timeout = setTimeout(() => request.abort(), 8000);
    try {
      const path = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      const url = new URL(path, window.location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store',
        // /cart negotiates plain cart JSON for application/json and then
        // ignores sections. Request the HTML route; sections still returns JSON.
        headers: { Accept: 'text/html' }, signal: request.signal
      });
      if (!response.ok) return;
      const sections = await response.json();
      if (pending !== request || request.signal.aborted || !content.isConnected || content.innerHTML !== before) return;
      // A section may be null even when the endpoint returns HTTP 200.
      if (typeof sections['cart-drawer'] !== 'string' || typeof sections['cart-icon-bubble'] !== 'string') return;
      const parser = new DOMParser();
      const incomingDrawer = parser.parseFromString(sections['cart-drawer'], 'text/html').querySelector('cart-drawer');
      const incomingContent = incomingDrawer?.querySelector('#CartDrawer');
      const incomingBubble = parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (!incomingContent || !incomingBubble) return;

      const wasOpen = drawer.classList.contains('active');
      const hadFocus = drawer.contains(document.activeElement);
      if (wasOpen && typeof removeTrapFocus === 'function') removeTrapFocus();
      content.innerHTML = incomingContent.innerHTML;
      drawer.classList.toggle('is-empty', incomingDrawer.classList.contains('is-empty'));
      bubble.innerHTML = incomingBubble.innerHTML;
      content.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());

      // Keep the shopper's current open/closed state. Calling open() or
      // renderContents() here would dispatch an extra cart-view event.
      if (wasOpen && typeof trapFocus === 'function') {
        const focusScope = drawer.classList.contains('is-empty') ? content.querySelector('.drawer__inner-empty') : content;
        if (focusScope) trapFocus(focusScope, hadFocus ? content.querySelector('.drawer__close') : undefined);
      }
    } catch (_) {
      // A failed read must never clear the cart or block the normal cart link.
    } finally {
      clearTimeout(timeout);
      if (pending === request) pending = null;
    }
  }
})();
